(() => {
  const MAX_BYTES = 4 * 1024 * 1024; // 4MB
  const THUMBS_VISIBLE = 3;

  // Brand logo (SVG) for exports
  const BRAND_LOGO_SVG_URL =
    "https://cdn.shopify.com/s/files/1/0679/9127/3662/files/logo-kotmo-1.svg?v=1758728222";

  // Disclaimer
  const DISCLAIMER_FR =
    "Ces visuels sont des simulations à des fins de présentation. Des variations de couleurs, dimensions, textures, positionnements et finitions peuvent survenir selon les matériaux, l’impression/broderie et les réglages d’affichage. Le produit final peut différer. Seules les spécifications et la validation finale font foi.";
  const DISCLAIMER_EN =
    "These visuals are simulations for presentation purposes only. Variations in color, dimensions, texture, positioning, and finishing may occur depending on materials, printing/embroidery processes, and display settings. The final product may differ. Only the final specifications and approval are binding.";

  let brandLogoPngCache = null; // { dataUrl, w, h }

  const state = {
    logoDataUrl: null,
    logoOriginalFile: null,
    logoFilename: null,
    entries: new Map(),
    instanceCounter: 0,
    manualCounter: 0
  };

  const el = {
    searchInput: document.getElementById("mgSearchInput"),
    searchResults: document.getElementById("mgSearchResults"),
    grid: document.getElementById("mgGrid"),
    logoInput: document.getElementById("mgLogoInput"),
    clearLogo: document.getElementById("mgClearLogo"),

    exportBtn: document.getElementById("mgExportBtn"),
    exportPdfCheck: document.getElementById("mgExportPdfCheck"),
    exportPngCheck: document.getElementById("mgExportPngCheck"),
    colsSelect: document.getElementById("mgPdfCols"),

    bgTol: document.getElementById("mgBgTol"),
    bgTolVal: document.getElementById("mgBgTolVal"),
    bgMsg: document.getElementById("mgBgMsg"),

    // ✅ Manual product inputs
    manualTitle: document.getElementById("mgManualTitle"),
    manualImages: document.getElementById("mgManualImages"),
    manualAddBtn: document.getElementById("mgManualAddBtn"),
  };

  if (!el.searchInput || !el.searchResults || !el.grid) return;

  // -------------------------
  // Exclusive export selection (PDF XOR PNG)
  // -------------------------
  enforceExclusiveExport();
  function enforceExclusiveExport() {
    const pdf = el.exportPdfCheck;
    const png = el.exportPngCheck;
    if (!pdf || !png) return;

    if (!pdf.checked && !png.checked) pdf.checked = true;

    const setPdf = () => { pdf.checked = true; png.checked = false; };
    const setPng = () => { png.checked = true; pdf.checked = false; };

    pdf.addEventListener("change", () => {
      if (pdf.checked) setPdf();
      else if (!png.checked) setPdf();
    });
    png.addEventListener("change", () => {
      if (png.checked) setPng();
      else if (!pdf.checked) setPng();
    });

    if (png.checked) setPng(); else setPdf();
  }

  function getCols() {
    const v = Number(el.colsSelect?.value || 3);
    return (v === 2 || v === 3 || v === 4) ? v : 3;
  }

  // -------------------------
  // Libs on demand
  // -------------------------
  function loadScriptOnce(src, testFn) {
    return new Promise((resolve, reject) => {
      if (testFn && testFn()) return resolve();
      const existing = document.querySelector(`script[data-mg-src="${encodeURIComponent(src)}"]`);
      if (existing) {
        const t = setInterval(() => {
          if (!testFn || testFn()) { clearInterval(t); resolve(); }
        }, 50);
        setTimeout(() => { clearInterval(t); reject(new Error(`Timeout loading ${src}`)); }, 8000);
        return;
      }
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.dataset.mgSrc = encodeURIComponent(src);
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed loading ${src}`));
      document.head.appendChild(s);
    });
  }

  async function ensureFabric() {
    if (window.fabric?.Canvas) return;
    await loadScriptOnce(
      "https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.0/fabric.min.js",
      () => !!window.fabric?.Canvas
    );
  }

  async function ensureJsPDF() {
    if (window.jspdf?.jsPDF) return;
    await loadScriptOnce(
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",
      () => !!window.jspdf?.jsPDF
    );
  }

  // -------------------------
  // Brand logo: SVG -> PNG cache
  // -------------------------
  async function ensureBrandLogoPng(targetWidthPx = 900) {
    if (brandLogoPngCache) return brandLogoPngCache;

    const res = await fetch(BRAND_LOGO_SVG_URL, { cache: "force-cache" });
    if (!res.ok) throw new Error("Logo fetch failed");
    let svgText = await res.text();
    if (!/xmlns=/.test(svgText)) {
      svgText = svgText.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }

    const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    await img.decode();
    URL.revokeObjectURL(url);

    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const scale = targetWidthPx / iw;

    const W = Math.max(1, Math.round(iw * scale));
    const H = Math.max(1, Math.round(ih * scale));

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, 0, 0, W, H);

    const dataUrl = canvas.toDataURL("image/png");
    brandLogoPngCache = { dataUrl, w: W, h: H };
    return brandLogoPngCache;
  }

  // -------------------------
  // BG removal controls (auto inject if missing)
  // -------------------------
  ensureBgRemovalControls();
  function ensureBgRemovalControls() {
    if (el.bgTol && el.bgTolVal && el.bgMsg) return;
    if (!el.clearLogo) return;

    const container = document.createElement("div");
    container.className = "mg-bg-controls";
    container.style.cssText = "display:flex;flex-direction:column;gap:6px;min-width:260px;max-width:340px;";

    container.innerHTML = `
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;">
        <label for="mgBgTol" style="font-size:13px;opacity:.85;font-weight:600;">Sensibilité du fond</label>
        <span id="mgBgTolVal" style="font-size:13px;opacity:.85;">30</span>
      </div>
      <input id="mgBgTol" type="range" min="10" max="70" step="1" value="30" style="width:320px;max-width:100%;" />
      <small style="opacity:.65;line-height:1.35;">Plus élevé = supprime plus (risque d’attaquer le logo).</small>
      <div id="mgBgMsg" style="min-height:18px;font-size:13px;opacity:.75;"></div>
    `;

    el.clearLogo.insertAdjacentElement("afterend", container);

    el.bgTol = document.getElementById("mgBgTol");
    el.bgTolVal = document.getElementById("mgBgTolVal");
    el.bgMsg = document.getElementById("mgBgMsg");

    const sync = () => (el.bgTolVal.textContent = String(Number(el.bgTol.value || 30)));
    sync();
    el.bgTol.addEventListener("input", sync);
  }

  function bgSetMsg(text) {
    if (!el.bgMsg) return;
    el.bgMsg.textContent = text || "";
  }

  // -------------------------
  // Predictive search
  // -------------------------
  let searchTimer = null;

  el.searchInput.addEventListener("input", () => {
    clearTimeout(searchTimer);
    const q = el.searchInput.value.trim();
    if (!q || q.length < 2) {
      el.searchResults.hidden = true;
      el.searchResults.innerHTML = "";
      return;
    }
    searchTimer = setTimeout(() => runPredictiveSearch(q), 200);
  });

  document.addEventListener("click", (e) => {
    if (!el.searchResults.contains(e.target) && e.target !== el.searchInput) el.searchResults.hidden = true;
  });

  async function runPredictiveSearch(q) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=10`;
      const res = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
      if (!res.ok) throw new Error(`Predictive search HTTP ${res.status}`);

      const json = await res.json();
      const products = json?.resources?.results?.products || [];

      el.searchResults.innerHTML = products.map(p => `
        <button class="mg-result" type="button" data-handle="${escapeAttr(p.handle)}">
          <span>${escapeHtml(p.title)}</span>
        </button>
      `).join("");
      el.searchResults.hidden = products.length === 0;

      el.searchResults.querySelectorAll("[data-handle]").forEach((node) => {
        node.addEventListener("click", async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const handle = node.dataset.handle;
          el.searchResults.hidden = true;
          el.searchInput.value = "";
          await addProductByHandle(handle);
        }, true);
      });
    } catch (e) {
      console.error("[Mockup] search error", e);
      el.searchResults.hidden = true;
    }
  }

  // -------------------------
  // ✅ Manual product add
  // -------------------------
  if (el.manualAddBtn && el.manualTitle && el.manualImages) {
    el.manualAddBtn.addEventListener("click", async () => {
      const title = (el.manualTitle.value || "").trim();
      const files = Array.from(el.manualImages.files || []);

      if (!title) return alert("Ajoute un titre pour le produit.");
      if (!files.length) return alert("Ajoute au moins une image produit.");

      // only images
      const imgFiles = files.filter(f => (f.type || "").startsWith("image/"));
      if (!imgFiles.length) return alert("Les fichiers doivent être des images.");

      // read to dataURLs
      const dataUrls = [];
      for (const f of imgFiles) {
        dataUrls.push(await readFileAsDataURL(f));
      }

      // reset UI
      el.manualTitle.value = "";
      el.manualImages.value = "";

      await addManualProduct(title, dataUrls);
    });
  }

  async function addManualProduct(title, imagesDataUrls) {
    const data = {
      id: `manual_${Date.now()}_${++state.manualCounter}`,
      title,
      handle: null,
      images: imagesDataUrls
    };

    const instanceId = `mg_${Date.now()}_${++state.instanceCounter}`;

    const card = renderProductCard(data);
    card.dataset.instanceId = instanceId;
    el.grid.appendChild(card);

    const entry = {
      instanceId,
      data,
      card,
      fabricCanvas: null,
      bgImage: null,
      logoObj: null,
      selectedImageIndex: 0,
      thumbStart: 0,
      visibleCount: THUMBS_VISIBLE,
      thumbSize: 86,
      thumbGap: 14
    };

    state.entries.set(instanceId, entry);

    wireCardUI(entry);
    initThumbCarousel(entry);

    await ensureFabric();
    entry.fabricCanvas = new fabric.Canvas(card.querySelector("canvas"), {
      preserveObjectStacking: true,
      selection: false,
    });

    await setBackgroundImage(entry, data.images[0]);

    if (state.logoDataUrl) {
      await setLogoOnCanvas(entry, state.logoDataUrl);
    }
  }

  // -------------------------
  // Logo upload
  // -------------------------
  if (el.logoInput) {
    el.logoInput.addEventListener("change", async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      state.logoOriginalFile = file;
      state.logoFilename = file.name || "logo";
      state.logoDataUrl = await readFileAsDataURL(file);
      bgSetMsg("");

      for (const entry of state.entries.values()) {
        if (entry.fabricCanvas) await setLogoOnCanvas(entry, state.logoDataUrl);
      }
    });
  }

  // -------------------------
  // Local background removal
  // -------------------------
  if (el.clearLogo) {
    el.clearLogo.addEventListener("click", async () => {
      try {
        if (!state.logoOriginalFile && !state.logoDataUrl) {
          alert("Ajoutez un logo d’abord.");
          return;
        }

        const file = state.logoOriginalFile || dataUrlToFile(state.logoDataUrl, state.logoFilename || "logo.png");
        const ext = extOf(file.name);

        if (!isRasterExt(ext) && file.type !== "image/png" && file.type !== "image/jpeg") {
          bgSetMsg("La suppression de fond fonctionne uniquement pour PNG/JPG. Pour un SVG, exportez en PNG/JPG.");
          return;
        }

        const tol = Number(el.bgTol?.value || 30);
        const btn = el.clearLogo;
        const prevText = btn.textContent;

        btn.disabled = true;
        btn.textContent = "Suppression…";
        bgSetMsg("Suppression du fond…");

        const outBlob = await removeBackgroundLocal(file, { tolerance: tol, feather: 1, maxSize: 1600 });

        const outFile = new File([outBlob], "logo-nobg.png", { type: "image/png" });
        state.logoOriginalFile = outFile;
        state.logoFilename = outFile.name;
        state.logoDataUrl = await blobToDataURL(outBlob);

        for (const entry of state.entries.values()) {
          if (entry.fabricCanvas) await setLogoOnCanvas(entry, state.logoDataUrl);
        }

        btn.textContent = prevText;
        btn.disabled = false;

        bgSetMsg("Fond supprimé ✅");
        setTimeout(() => bgSetMsg(""), 1200);
      } catch (e) {
        console.error("[Mockup] remove bg local error", e);
        if (el.clearLogo) {
          el.clearLogo.disabled = false;
          el.clearLogo.textContent = "Supprimer le fond";
        }
        bgSetMsg("Échec suppression de fond.");
      }
    });
  }

  // -------------------------
  // Product data fetch (Priority: section_id first)
  // -------------------------
  async function fetchProductData(handle) {
    try {
      const data = await fetchViaSectionId(handle);
      if (data?.id && Array.isArray(data.images) && data.images.length) return data;
    } catch {}
    try {
      const data = await fetchViaSectionsApi(handle);
      if (data?.id && Array.isArray(data.images) && data.images.length) return data;
    } catch {}
    try {
      const data = await fetchViaView(handle);
      if (data?.id && Array.isArray(data.images) && data.images.length) return data;
    } catch {}

    const pj = await fetchProductJs(handle);
    return { id: pj.id, title: pj.title, handle, images: pj.images || [] };
  }

  async function fetchViaSectionId(handle) {
    const url = `/products/${handle}?section_id=mockup-product-data&_ts=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: "text/html" }, cache: "no-store" });
    const html = await res.text();
    return parseProductJsonFromHtml(html);
  }

  async function fetchViaSectionsApi(handle) {
    const expectedKey = "mockup-product-data";
    const url = `/products/${handle}?sections=${expectedKey}&_ts=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
    const json = await res.json();
    const mapping = json?.sections && typeof json.sections === "object" ? json.sections : json;
    const html = mapping?.[expectedKey];
    if (typeof html !== "string") throw new Error("HTML invalide dans sections API");
    return parseProductJsonFromHtml(html);
  }

  async function fetchViaView(handle) {
    const url = `/products/${handle}?view=mockup-json&_ts=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
    const raw = JSON.parse(await res.text());
    const data = unwrap(raw);
    return { id: data.id, title: data.title, handle: data.handle || handle, images: Array.isArray(data.images) ? data.images : [] };
  }

  async function fetchProductJs(handle) {
    const url = `/products/${handle}.js?_ts=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
    return await res.json();
  }

  function parseProductJsonFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const script = doc.querySelector("script[data-mg-product-json]");
    if (!script) throw new Error("script[data-mg-product-json] introuvable");
    const data = JSON.parse(script.textContent || "{}");
    data.images = normalizeImages(Array.isArray(data.images) ? data.images : []);
    if (!data.id) throw new Error("id manquant");
    if (!data.images.length) throw new Error("images vides");
    return data;
  }

  function unwrap(raw) {
    if (raw && typeof raw === "object") {
      if (raw.product && typeof raw.product === "object") return raw.product;
      if (raw.data && typeof raw.data === "object") return raw.data;
      if (raw.payload && typeof raw.payload === "object") return raw.payload;
      if (raw.result && typeof raw.result === "object") return raw.result;
    }
    return raw;
  }

  // -------------------------
  // Add Shopify product (duplicates allowed)
  // -------------------------
  async function addProductByHandle(handle) {
    const data = await fetchProductData(handle);
    data.images = normalizeImages(Array.isArray(data.images) ? data.images : []);
    if (!data.id) throw new Error("Impossible de récupérer un id produit.");
    if (!data.images.length) throw new Error("Aucune image exploitable.");

    const instanceId = `mg_${Date.now()}_${++state.instanceCounter}`;
    const card = renderProductCard(data);
    card.dataset.instanceId = instanceId;
    el.grid.appendChild(card);

    const entry = {
      instanceId,
      data,
      card,
      fabricCanvas: null,
      bgImage: null,
      logoObj: null,
      selectedImageIndex: 0,
      thumbStart: 0,
      visibleCount: THUMBS_VISIBLE,
      thumbSize: 86,
      thumbGap: 14,
    };

    state.entries.set(instanceId, entry);

    wireCardUI(entry);
    initThumbCarousel(entry);

    await ensureFabric();
    entry.fabricCanvas = new fabric.Canvas(card.querySelector("canvas"), {
      preserveObjectStacking: true,
      selection: false,
    });

    await setBackgroundImage(entry, data.images[0]);
    if (state.logoDataUrl) await setLogoOnCanvas(entry, state.logoDataUrl);
  }

  // -------------------------
  // Card rendering + UI
  // -------------------------
  function renderProductCard(data) {
    const wrap = document.createElement("div");
    wrap.className = "mg-card";

    const thumbs = (data.images || []).map((src, idx) => `
      <button type="button" class="mg-thumb ${idx === 0 ? "is-active" : ""}" data-idx="${idx}">
        <img src="${escapeAttr(src)}" alt="" loading="lazy" />
      </button>
    `).join("");

    wrap.innerHTML = `
      <div class="mg-card-header">
        <div class="mg-title">${escapeHtml(data.title || data.handle || "Produit")}</div>
        <button class="mg-remove" type="button" aria-label="Retirer">×</button>
      </div>

      <div class="mg-canvas-wrap" style="position:relative;">
        <canvas width="360" height="360"></canvas>
        <div data-mg-error class="mg-error"></div>
      </div>

      <div class="mg-thumbs">
        <button type="button" class="mg-arrow mg-arrow-prev" data-mg-prev aria-label="Précédent">‹</button>
        <div class="mg-thumbs-viewport" data-mg-viewport>
          <div class="mg-thumbs-track" data-mg-track>
            ${thumbs}
          </div>
        </div>
        <button type="button" class="mg-arrow mg-arrow-next" data-mg-next aria-label="Suivant">›</button>
      </div>

      <div class="mg-controls">
        <label>Logo taille</label>
        <input class="mg-scale" type="range" min="10" max="300" value="100" />
        <label>Logo rotation</label>
        <input class="mg-rotate" type="range" min="-180" max="180" value="0" />
      </div>
    `;
    return wrap;
  }

  function wireCardUI(entry) {
    entry.card.querySelector(".mg-remove")?.addEventListener("click", () => {
      entry.fabricCanvas?.dispose?.();
      state.entries.delete(entry.instanceId);
      entry.card.remove();
    });

    entry.card.querySelectorAll(".mg-thumb").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const idx = Number(btn.dataset.idx);
        entry.selectedImageIndex = idx;

        entry.card.querySelectorAll(".mg-thumb").forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");

        if (entry.fabricCanvas) await setBackgroundImage(entry, entry.data.images[idx]);
        ensureThumbVisible(entry, idx);
      });
    });

    entry.card.querySelector("[data-mg-prev]")?.addEventListener("click", (e) => {
      e.preventDefault(); e.stopPropagation();
      scrollThumbs(entry, -entry.visibleCount);
    });

    entry.card.querySelector("[data-mg-next]")?.addEventListener("click", (e) => {
      e.preventDefault(); e.stopPropagation();
      scrollThumbs(entry, +entry.visibleCount);
    });

    const scaleInput = entry.card.querySelector(".mg-scale");
    const rotateInput = entry.card.querySelector(".mg-rotate");

    scaleInput?.addEventListener("input", () => {
      if (!entry.logoObj) return;
      entry.logoObj.scale(Number(scaleInput.value) / 100);
      entry.fabricCanvas.requestRenderAll();
    });

    rotateInput?.addEventListener("input", () => {
      if (!entry.logoObj) return;
      entry.logoObj.rotate(Number(rotateInput.value));
      entry.fabricCanvas.requestRenderAll();
    });

    const viewport = entry.card.querySelector("[data-mg-viewport]");
    if (viewport) new ResizeObserver(() => updateThumbCarousel(entry)).observe(viewport);
  }

  function initThumbCarousel(entry) {
    entry.thumbStart = 0;
    updateThumbCarousel(entry);
    ensureThumbVisible(entry, entry.selectedImageIndex || 0);
  }

  function updateThumbCarousel(entry) {
    const viewport = entry.card.querySelector("[data-mg-viewport]");
    const track = entry.card.querySelector("[data-mg-track]");
    if (!viewport || !track) return;

    const first = track.querySelector(".mg-thumb");
    if (first) {
      entry.thumbSize = Math.round(first.getBoundingClientRect().width) || entry.thumbSize;
      const cs = getComputedStyle(track);
      entry.thumbGap = parseFloat(cs.gap || cs.columnGap || "0") || entry.thumbGap;
    }

    entry.visibleCount = THUMBS_VISIBLE;
    const viewportW = entry.visibleCount * entry.thumbSize + (entry.visibleCount - 1) * entry.thumbGap;
    viewport.style.maxWidth = `${viewportW}px`;
    viewport.style.width = `${viewportW}px`;

    clampThumbStart(entry);
    applyThumbTransform(entry);
    updateArrowState(entry);
  }

  function clampThumbStart(entry) {
    const total = entry.data.images.length;
    const maxStart = Math.max(0, total - entry.visibleCount);
    entry.thumbStart = Math.max(0, Math.min(entry.thumbStart, maxStart));
  }

  function applyThumbTransform(entry) {
    const track = entry.card.querySelector("[data-mg-track]");
    if (!track) return;
    const step = entry.thumbSize + entry.thumbGap;
    track.style.transform = `translateX(${-entry.thumbStart * step}px)`;
  }

  function updateArrowState(entry) {
    const prev = entry.card.querySelector("[data-mg-prev]");
    const next = entry.card.querySelector("[data-mg-next]");
    const total = entry.data.images.length;
    const maxStart = Math.max(0, total - entry.visibleCount);
    const show = total > entry.visibleCount;

    if (prev) { prev.disabled = entry.thumbStart <= 0; prev.style.display = show ? "" : "none"; }
    if (next) { next.disabled = entry.thumbStart >= maxStart; next.style.display = show ? "" : "none"; }
  }

  function scrollThumbs(entry, delta) {
    entry.thumbStart += delta;
    clampThumbStart(entry);
    applyThumbTransform(entry);
    updateArrowState(entry);
  }

  function ensureThumbVisible(entry, idx) {
    const start = entry.thumbStart;
    const end = start + entry.visibleCount - 1;

    if (idx < start) {
      entry.thumbStart = idx;
      clampThumbStart(entry);
      applyThumbTransform(entry);
      updateArrowState(entry);
      return;
    }
    if (idx > end) {
      entry.thumbStart = idx - (entry.visibleCount - 1);
      clampThumbStart(entry);
      applyThumbTransform(entry);
      updateArrowState(entry);
    }
  }

  // -------------------------
  // Fabric helpers
  // -------------------------
  async function setBackgroundImage(entry, imgUrl) {
    const canvas = entry.fabricCanvas;
    const bg = await loadFabricImage(imgUrl);

    const cw = canvas.getWidth();
    const ch = canvas.getHeight();
    const scale = Math.max(cw / bg.width, ch / bg.height);

    bg.set({
      left: cw / 2,
      top: ch / 2,
      originX: "center",
      originY: "center",
      selectable: false,
      evented: false,
      scaleX: scale,
      scaleY: scale,
    });

    if (entry.bgImage) canvas.remove(entry.bgImage);
    entry.bgImage = bg;
    canvas.insertAt(bg, 0, false);
    canvas.requestRenderAll();
  }

  async function setLogoOnCanvas(entry, logoDataUrl) {
    const canvas = entry.fabricCanvas;
    const logo = await loadFabricImage(logoDataUrl);

    if (entry.logoObj) canvas.remove(entry.logoObj);

    const cw = canvas.getWidth();
    const targetW = cw * 0.35;
    const s = targetW / logo.width;

    logo.set({
      left: cw / 2,
      top: canvas.getHeight() / 2,
      originX: "center",
      originY: "center",
      scaleX: s,
      scaleY: s,
      selectable: true,
      hasControls: true,
    });

    logo.setControlsVisibility({ mt:false, mb:false, ml:false, mr:false, tl:true, tr:true, bl:true, br:true, mtr:true });

    entry.logoObj = logo;
    canvas.add(logo);
    canvas.requestRenderAll();
  }

  function loadFabricImage(urlOrDataUrl) {
    return new Promise((resolve, reject) => {
      const u = absolutizeUrl(urlOrDataUrl);
      fabric.Image.fromURL(u, (img) => (img ? resolve(img) : reject(new Error("Image load failed"))), {
        crossOrigin: "anonymous",
      });
    });
  }

  // -------------------------
  // Export (PDF + PNG) logo header + disclaimer footer
  // -------------------------
  if (el.exportBtn) {
    el.exportBtn.addEventListener("click", async () => {
      const pdf = !!el.exportPdfCheck?.checked;
      const png = !!el.exportPngCheck?.checked;
      if (pdf === png) return alert("Choisis soit PDF, soit PNG.");
      if (state.entries.size === 0) return;

      try {
        if (pdf) await exportPDFUnder4MB();
        if (png) await exportPNGSheetUnder4MB();
      } catch (e) {
        console.error(e);
        alert("Erreur pendant l'export.");
      }
    });
  }

  async function exportPDFUnder4MB() {
    await ensureJsPDF();
    const { jsPDF } = window.jspdf;
    const cols = getCols();
    const brand = await ensureBrandLogoPng(900);

    const attempts = [
      { multiplier: 2.0, quality: 0.86 },
      { multiplier: 1.5, quality: 0.82 },
      { multiplier: 1.25, quality: 0.78 },
      { multiplier: 1.0, quality: 0.75 },
      { multiplier: 0.85, quality: 0.72 },
    ];

    for (const a of attempts) {
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const gap = 5;

      const headerH = 16;
      const footerH = 18;

      const usableTop = margin + headerH;
      const usableBottom = pageH - margin - footerH;

      const cellW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
      const cellH = cellW;

      let i = 0;
      let x = margin;
      let y = usableTop;

      for (const entry of state.entries.values()) {
        if (!entry.fabricCanvas) continue;

        const imgData = entry.fabricCanvas.toDataURL({
          format: "jpeg",
          quality: a.quality,
          multiplier: a.multiplier,
        });

        pdf.addImage(imgData, "JPEG", x, y, cellW, cellH);

        i++;
        if (i % cols === 0) {
          x = margin;
          y += cellH + gap;

          if (y + cellH > usableBottom) {
            pdf.addPage();
            y = usableTop;
          }
        } else {
          x += cellW + gap;
        }
      }

      addPdfHeaderLogoOnAllPages(pdf, margin, brand);
      addPdfDisclaimerOnAllPages(pdf, pageW, pageH, margin);

      const buf = pdf.output("arraybuffer");
      if (buf.byteLength <= MAX_BYTES) {
        pdf.save("mockups.pdf");
        return;
      }
    }

    alert("Le PDF dépasse 4 Mo même après compression. Essaie moins d’images ou augmente Images / ligne.");
  }

  function addPdfHeaderLogoOnAllPages(pdf, margin, brand) {
    const totalPages = pdf.getNumberOfPages();
    const logoWmm = 30;
    const logoHmm = (logoWmm * brand.h) / brand.w;

    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      pdf.addImage(brand.dataUrl, "PNG", margin, margin, logoWmm, logoHmm);
    }
  }

  function addPdfDisclaimerOnAllPages(pdf, pageW, pageH, margin) {
    const totalPages = pdf.getNumberOfPages();
    const maxWidth = pageW - margin * 2;

    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);

      const frLines = pdf.splitTextToSize(`FR: ${DISCLAIMER_FR}`, maxWidth);
      const enLines = pdf.splitTextToSize(`EN: ${DISCLAIMER_EN}`, maxWidth);

      const lineH = 3.4;
      const blockH = (frLines.length + enLines.length) * lineH;

      let y = pageH - margin - blockH + 2;
      pdf.setTextColor(60);

      frLines.forEach((ln) => { pdf.text(ln, margin, y); y += lineH; });
      y += 0.8;
      enLines.forEach((ln) => { pdf.text(ln, margin, y); y += lineH; });
    }
  }

  async function exportPNGSheetUnder4MB() {
    const cols = getCols();
    const items = Array.from(state.entries.values()).filter((e) => !!e.fabricCanvas);
    if (!items.length) return;

    const brand = await ensureBrandLogoPng(900);
    const attempts = [2.0, 1.5, 1.25, 1.0, 0.85];

    for (const mult of attempts) {
      const blob = await buildPngSheetBlob(items, cols, mult, true, true, brand);
      if (blob.size <= MAX_BYTES) {
        downloadBlob(blob, "mockups_planche.png");
        return;
      }
    }

    let blob = await buildPngSheetBlob(items, cols, 0.85, true, true, brand);
    let scale = 0.85;

    while (blob.size > MAX_BYTES && scale > 0.35) {
      scale *= 0.85;
      blob = await downscalePngBlob(blob, scale);
    }

    if (blob.size <= MAX_BYTES) {
      downloadBlob(blob, "mockups_planche.png");
      return;
    }

    alert("Le PNG dépasse 4 Mo même après réduction. Essaie moins d’images ou augmente Images / ligne.");
  }

  async function buildPngSheetBlob(items, cols, multiplier, withDisclaimer, withHeaderLogo, brand) {
    const dataUrls = items.map((e) => e.fabricCanvas.toDataURL({ format: "png", multiplier }));
    const firstImg = await loadHtmlImage(dataUrls[0]);

    const cellW = firstImg.naturalWidth || firstImg.width;
    const cellH = firstImg.naturalHeight || firstImg.height;

    const gap = Math.round(cellW * 0.05);
    const margin = gap;
    const rows = Math.ceil(dataUrls.length / cols);

    const gridW = margin * 2 + cols * cellW + (cols - 1) * gap;
    const gridH = margin * 2 + rows * cellH + (rows - 1) * gap;

    const headerPad = Math.round(gap * 0.9);
    let headerH = 0, logoDrawW = 0, logoDrawH = 0;

    if (withHeaderLogo && brand) {
      logoDrawW = Math.min(Math.round(gridW * 0.22), 520);
      logoDrawH = Math.round((logoDrawW * brand.h) / brand.w);
      headerH = headerPad * 2 + logoDrawH;
    }

    const footerPad = Math.round(gap * 0.9);
    const footerFont = Math.max(16, Math.min(22, Math.round(cellW * 0.07)));
    const footerLineH = Math.round(footerFont * 1.25);
    const footerH = withDisclaimer ? footerPad * 2 + footerLineH * 5 : 0;

    const sheetW = gridW;
    const sheetH = headerH + gridH + footerH;

    const canvas = document.createElement("canvas");
    canvas.width = sheetW;
    canvas.height = sheetH;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, sheetW, sheetH);

    let yOffset = 0;

    if (withHeaderLogo && brand) {
      const logoImg = await loadHtmlImage(brand.dataUrl);
      ctx.drawImage(logoImg, margin, headerPad, logoDrawW, logoDrawH);
      yOffset = headerH;
    }

    for (let i = 0; i < dataUrls.length; i++) {
      const img = i === 0 ? firstImg : await loadHtmlImage(dataUrls[i]);
      const r = Math.floor(i / cols);
      const c = i % cols;
      const x = margin + c * (cellW + gap);
      const y = yOffset + margin + r * (cellH + gap);
      ctx.drawImage(img, x, y, cellW, cellH);
    }

    if (withDisclaimer) {
      const startY = yOffset + gridH + footerPad;
      drawCanvasDisclaimer(ctx, margin, startY, sheetW - margin * 2, footerFont, footerLineH);
    }

    return await canvasToPngBlob(canvas);
  }

  function drawCanvasDisclaimer(ctx, x, y, maxWidth, fontPx, lineH) {
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.70)";
    ctx.font = `${fontPx}px Arial`;
    ctx.textBaseline = "top";

    const frLines = wrapTextLines(ctx, `FR: ${DISCLAIMER_FR}`, maxWidth);
    const enLines = wrapTextLines(ctx, `EN: ${DISCLAIMER_EN}`, maxWidth);

    let yy = y;
    frLines.forEach((ln) => { ctx.fillText(ln, x, yy); yy += lineH; });
    yy += Math.round(lineH * 0.4);
    enLines.forEach((ln) => { ctx.fillText(ln, x, yy); yy += lineH; });

    ctx.restore();
  }

  function wrapTextLines(ctx, text, maxWidth) {
    const words = String(text).split(/\s+/);
    const lines = [];
    let line = "";
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width <= maxWidth) line = test;
      else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  }

  async function downscalePngBlob(blob, scale) {
    const img = await blobToImage(blob);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    return await canvasToPngBlob(canvas);
  }

  function canvasToPngBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
    });
  }

  function blobToImage(blob) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function loadHtmlImage(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  // -------------------------
  // Utils
  // -------------------------
  function normalizeImages(images) {
    if (!Array.isArray(images)) return [];
    const out = [];
    for (const it of images) {
      if (typeof it !== "string") continue;
      const s = absolutizeUrl(it);
      if (s) out.push(s);
    }
    return out;
  }

  function absolutizeUrl(u) {
    let s = String(u || "").trim();
    if (!s) return "";
    if (s.startsWith("data:")) return s; // ✅ keep data URLs for manual products
    if (s.startsWith("//")) s = `https:${s}`;
    if (s.startsWith("/")) s = `${location.origin}${s}`;
    return s.replace(/\s+/g, "");
  }

  function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }

  function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  function dataUrlToFile(dataUrl, filename) {
    const arr = String(dataUrl).split(",");
    const mime = (arr[0].match(/:(.*?);/) || [])[1] || "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    return new File([u8], filename || "logo.png", { type: mime });
  }

  function extOf(name = "") {
    const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : "";
  }
  const isRasterExt = (ext) => ext === "png" || ext === "jpg" || ext === "jpeg";

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (m) => ({
      "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#039;"
    }[m]));
  }
  function escapeAttr(str) { return String(str).replace(/"/g, "&quot;"); }

  // -------------------------
  // Local background removal (unchanged)
  // -------------------------
  function rgbDist(a, b) {
    const dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }
  function sampleAvgColor(imgData, x, y, radius = 2) {
    const { data, width, height } = imgData;
    let r = 0, g = 0, b = 0, n = 0;
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const xx = Math.max(0, Math.min(width - 1, x + dx));
        const yy = Math.max(0, Math.min(height - 1, y + dy));
        const i = (yy * width + xx) * 4;
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
      }
    }
    return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
  }
  function decontaminateWhiteHalo(imgData, strength = 0.9) {
    const { data } = imgData;
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a === 0 || a === 255) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const whiteness = Math.min(1, (r + g + b) / (3 * 255));
      const k = 1 - whiteness * strength;
      data[i] = Math.round(r * k);
      data[i + 1] = Math.round(g * k);
      data[i + 2] = Math.round(b * k);
    }
  }
  function erodeAlpha(imgData, iterations = 1) {
    const { data, width, height } = imgData;
    const idx = (x, y) => (y * width + x) * 4;
    for (let it = 0; it < iterations; it++) {
      const copy = new Uint8ClampedArray(data);
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const i = idx(x, y);
          const a = copy[i + 3];
          if (a === 0) continue;
          const n1 = copy[idx(x - 1, y) + 3];
          const n2 = copy[idx(x + 1, y) + 3];
          const n3 = copy[idx(x, y - 1) + 3];
          const n4 = copy[idx(x, y + 1) + 3];
          if (n1 === 0 || n2 === 0 || n3 === 0 || n4 === 0) data[i + 3] = Math.max(0, a - 35);
        }
      }
    }
  }
  async function removeBackgroundLocal(file, opts = {}) {
    const { tolerance = 30, feather = 1, maxSize = 1600 } = opts;

    const blobUrl = URL.createObjectURL(file);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = blobUrl;
    await img.decode();

    const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
    const W = Math.max(1, Math.round(img.width * scale));
    const H = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    URL.revokeObjectURL(blobUrl);

    const imgData = ctx.getImageData(0, 0, W, H);
    const data = imgData.data;

    const bgs = [
      sampleAvgColor(imgData, 0, 0),
      sampleAvgColor(imgData, W - 1, 0),
      sampleAvgColor(imgData, 0, H - 1),
      sampleAvgColor(imgData, W - 1, H - 1),
    ];

    const isBgLikeIdx = (i) => {
      const px = [data[i], data[i + 1], data[i + 2]];
      for (const bg of bgs) if (rgbDist(px, bg) <= tolerance) return true;
      return false;
    };

    const visited = new Uint8Array(W * H);
    const queue = new Int32Array(W * H);
    let qh = 0, qt = 0;

    const push = (x, y) => {
      const p = y * W + x;
      if (visited[p]) return;
      visited[p] = 1;
      queue[qt++] = p;
    };
    const isBgLikeXY = (x, y) => isBgLikeIdx((y * W + x) * 4);

    push(0, 0); push(W - 1, 0); push(0, H - 1); push(W - 1, H - 1);

    while (qh < qt) {
      const p = queue[qh++];
      const x = p % W;
      const y = (p / W) | 0;
      if (!isBgLikeXY(x, y)) continue;
      const i = p * 4;
      data[i + 3] = 0;
      if (x > 0) push(x - 1, y);
      if (x < W - 1) push(x + 1, y);
      if (y > 0) push(x, y - 1);
      if (y < H - 1) push(x, y + 1);
    }

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      if (isBgLikeIdx(i)) data[i + 3] = 0;
    }

    if (feather > 0) {
      const copy = new Uint8ClampedArray(data);
      const rad = Math.min(3, Math.max(1, feather | 0));
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const p = y * W + x;
          const i = p * 4;
          if (copy[i + 3] !== 0) continue;
          for (let dy = -rad; dy <= rad; dy++) {
            for (let dx = -rad; dx <= rad; dx++) {
              const xx = x + dx, yy = y + dy;
              if (xx < 0 || xx >= W || yy < 0 || yy >= H) continue;
              const j = (yy * W + xx) * 4;
              if (data[j + 3] > 0) data[j + 3] = Math.min(data[j + 3], 220);
            }
          }
        }
      }
    }

    decontaminateWhiteHalo(imgData, 0.9);
    erodeAlpha(imgData, 1);

    ctx.putImageData(imgData, 0, 0);
    const outBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
    if (!outBlob) throw new Error("toBlob failed");
    return outBlob;
  }
})();